/* tslint:disable */

import { ITextEditorSelection } from '@vscode-alt/monaco-editor/esm/vs/platform/editor/common/editor';
import { IEditor as ICodeEditor, IEditorViewState, ScrollType, IEditor } from '@vscode-alt/monaco-editor/esm/vs/editor/common/editorCommon';
import { IRange } from '@vscode-alt/monaco-editor/esm/vs/editor/common/core/range';
import { IBaseResourceInput, IEditorOptions, 
    IEditorActivation as EditorActivation, 
    IEditorOpenContext as EditorOpenContext,
    ITextEditorOptions_Input as ITextEditorOptions,
    ITextEditorSelectionRevealType as TextEditorSelectionRevealType,
    
} from '@workbench-stack/core';



// src/vs/workbench/browser/parts/editor/editor.ts

export const DEFAULT_EDITOR_PART_OPTIONS: IEditorPartOptions = {
    showTabs: true,
    highlightModifiedTabs: false,
    tabCloseButton: 'right',
    tabSizing: 'fit',
    focusRecentEditorAfterClose: true,
    showIcons: true,
    enablePreview: true,
    openPositioning: 'right',
    openSideBySideDirection: 'right',
    closeEmptyGroups: true,
    labelFormat: 'default',
    iconTheme: 'vs-seti'
};


export function getEditorPartOptions(config: IWorkbenchEditorConfiguration): IEditorPartOptions {
    const options: IEditorPartOptions = Object.assign(Object.create(null), DEFAULT_EDITOR_PART_OPTIONS);

    if (!config || !config.workbench) {
        return options;
    }

    if (typeof config.workbench.iconTheme === 'string') {
        options.iconTheme = config.workbench.iconTheme;
    }

    if (config.workbench.editor) {
        Object.assign(options, config.workbench.editor);
    }

    return options;
}


// src/vs/workbench/common/editor.ts

export interface IWorkbenchEditorConfiguration {
    workbench: {
        editor: IEditorPartConfiguration,
        iconTheme: string;
    };
}
interface IEditorPartConfiguration {
    showTabs?: boolean;
    highlightModifiedTabs?: boolean;
    tabCloseButton?: 'left' | 'right' | 'off';
    tabSizing?: 'fit' | 'shrink';
    focusRecentEditorAfterClose?: boolean;
    showIcons?: boolean;
    enablePreview?: boolean;
    enablePreviewFromQuickOpen?: boolean;
    closeOnFileDelete?: boolean;
    openPositioning?: 'left' | 'right' | 'first' | 'last';
    openSideBySideDirection?: 'right' | 'down';
    closeEmptyGroups?: boolean;
    revealIfOpen?: boolean;
    swipeToNavigate?: boolean;
    labelFormat?: 'default' | 'short' | 'medium' | 'long';
    restoreViewState?: boolean;
}

export interface IEditorPartOptions extends IEditorPartConfiguration {
    iconTheme?: string;
}


/**
 * The editor options is the base class of options that can be passed in when opening an editor.
 */
export class EditorOptions implements IEditorOptions {

	/**
	 * Helper to create EditorOptions inline.
	 */
	static create(settings: IEditorOptions): EditorOptions {
		const options = new EditorOptions();
		options.overwrite(settings);

		return options;
	}

	/**
	 * Tells the editor to not receive keyboard focus when the editor is being opened.
	 *
	 * Will also not activate the group the editor opens in unless the group is already
	 * the active one. This behaviour can be overridden via the `activation` option.
	 */
	preserveFocus: boolean | undefined;

	/**
	 * This option is only relevant if an editor is opened into a group that is not active
	 * already and allows to control if the inactive group should become active, restored
	 * or preserved.
	 *
	 * By default, the editor group will become active unless `preserveFocus` or `inactive`
	 * is specified.
	 */
	activation: EditorActivation | undefined;

	/**
	 * Tells the editor to reload the editor input in the editor even if it is identical to the one
	 * already showing. By default, the editor will not reload the input if it is identical to the
	 * one showing.
	 */
	forceReload: boolean | undefined;

	/**
	 * Will reveal the editor if it is already opened and visible in any of the opened editor groups.
	 */
	revealIfVisible: boolean | undefined;

	/**
	 * Will reveal the editor if it is already opened (even when not visible) in any of the opened editor groups.
	 */
	revealIfOpened: boolean | undefined;

	/**
	 * An editor that is pinned remains in the editor stack even when another editor is being opened.
	 * An editor that is not pinned will always get replaced by another editor that is not pinned.
	 */
	pinned: boolean | undefined;

	/**
	 * An editor that is sticky moves to the beginning of the editors list within the group and will remain
	 * there unless explicitly closed. Operations such as "Close All" will not close sticky editors.
	 */
	sticky: boolean | undefined;

	/**
	 * The index in the document stack where to insert the editor into when opening.
	 */
	index: number | undefined;

	/**
	 * An active editor that is opened will show its contents directly. Set to true to open an editor
	 * in the background without loading its contents.
	 *
	 * Will also not activate the group the editor opens in unless the group is already
	 * the active one. This behaviour can be overridden via the `activation` option.
	 */
	inactive: boolean | undefined;

	/**
	 * Will not show an error in case opening the editor fails and thus allows to show a custom error
	 * message as needed. By default, an error will be presented as notification if opening was not possible.
	 */
	ignoreError: boolean | undefined;

	/**
	 * Allows to override the editor that should be used to display the input:
	 * - `undefined`: let the editor decide for itself
	 * - `false`: disable overrides
	 * - `string`: specific override by id
	 */
	override?: false | string;

	/**
	 * A optional hint to signal in which context the editor opens.
	 *
	 * If configured to be `EditorOpenContext.USER`, this hint can be
	 * used in various places to control the experience. For example,
	 * if the editor to open fails with an error, a notification could
	 * inform about this in a modal dialog. If the editor opened through
	 * some background task, the notification would show in the background,
	 * not as a modal dialog.
	 */
	context: EditorOpenContext | undefined;

	/**
	 * Overwrites option values from the provided bag.
	 */
	overwrite(options: IEditorOptions): EditorOptions {
		if (typeof options.forceReload === 'boolean') {
			this.forceReload = options.forceReload;
		}

		if (typeof options.revealIfVisible === 'boolean') {
			this.revealIfVisible = options.revealIfVisible;
		}

		if (typeof options.revealIfOpened === 'boolean') {
			this.revealIfOpened = options.revealIfOpened;
		}

		if (typeof options.preserveFocus === 'boolean') {
			this.preserveFocus = options.preserveFocus;
		}

		if (typeof options.activation === 'number') {
			this.activation = options.activation;
		}

		if (typeof options.pinned === 'boolean') {
			this.pinned = options.pinned;
		}

		if (typeof options.sticky === 'boolean') {
			this.sticky = options.sticky;
		}

		if (typeof options.inactive === 'boolean') {
			this.inactive = options.inactive;
		}

		if (typeof options.ignoreError === 'boolean') {
			this.ignoreError = options.ignoreError;
		}

		if (typeof options.index === 'number') {
			this.index = options.index;
		}

		if (typeof options.override === 'string' || options.override === false) {
			this.override = options.override;
		}

		if (typeof options.context === 'number') {
			this.context = options.context;
		}

		return this;
	}
}

/**
 * Base Text Editor Optionsπ
 */
export class TextEditorOptions extends EditorOptions implements ITextEditorOptions {
	/**
	 * Text editor selection.
	 */
	selection: ITextEditorSelection | undefined;

	/**
	 * Text editor view state.
	 */
    editorViewState: IEditorViewState | undefined;
    
    /**
	 * Option to control the text editor selection reveal type.
	 */
	selectionRevealType: TextEditorSelectionRevealType | undefined;

    static from(input?: IBaseResourceInput): TextEditorOptions | undefined {
        if (!input || !input.options) {
            return undefined;
        }

        return TextEditorOptions.create(input.options);
    }

    /**
     * Helper to convert options bag to real class
     */
    static create(options: ITextEditorOptions = Object.create(null)): TextEditorOptions {
        const textEditorOptions = new TextEditorOptions();

        textEditorOptions.overwrite(options);

        return textEditorOptions;
    }

    /**
     * Overwrites option values from the provided bag.
     */
    overwrite(options: ITextEditorOptions): TextEditorOptions {
        super.overwrite(options);

        if (options.selection) {
            this.selection = {
                startLineNumber: options.selection.startLineNumber,
                startColumn: options.selection.startColumn,
                endLineNumber: options.selection.endLineNumber ?? options.selection.startLineNumber,
                endColumn: options.selection.endColumn ?? options.selection.startColumn
            };
        }

        if (options.viewState) {
            this.editorViewState = options.viewState as IEditorViewState;
        }

        if (typeof options.selectionRevealType !== 'undefined') {
            this.selectionRevealType = options.selectionRevealType;
        }

        return this;
    }

	/**
	 * Returns if this options object has objects defined for the editor.
	 */
	hasOptionsDefined(): boolean {
		return !!this.editorViewState || !!this.selectionRevealType || !!this.selection;
	}

    /**
     * Create a TextEditorOptions inline to be used when the editor is opening.
     */
    static fromEditor(editor: ICodeEditor, settings?: IEditorOptions): TextEditorOptions {
        const options = TextEditorOptions.create(settings);

        // View state
        options.editorViewState = editor.saveViewState();

        return options;
    }

	/**
	 * Apply the view state or selection to the given editor.
	 *
	 * @return if something was applied
	 */
	apply(editor: IEditor, scrollType: ScrollType): boolean {
		let gotApplied = false;

		// First try viewstate
		if (this.editorViewState) {
			editor.restoreViewState(this.editorViewState);
			gotApplied = true;
		}

		// Otherwise check for selection
		else if (this.selection) {
			const range: IRange = {
				startLineNumber: this.selection.startLineNumber,
				startColumn: this.selection.startColumn,
				endLineNumber: this.selection.endLineNumber ?? this.selection.startLineNumber,
				endColumn: this.selection.endColumn ?? this.selection.startColumn
			};

			editor.setSelection(range);

			if (this.selectionRevealType === TextEditorSelectionRevealType.NearTop) {
				editor.revealRangeNearTop(range, scrollType);
			} else if (this.selectionRevealType === TextEditorSelectionRevealType.NearTopIfOutsideViewport) {
				editor.revealRangeNearTopIfOutsideViewport(range, scrollType);
			} else if (this.selectionRevealType === TextEditorSelectionRevealType.CenterIfOutsideViewport) {
				editor.revealRangeInCenterIfOutsideViewport(range, scrollType);
			} else {
				editor.revealRangeInCenter(range, scrollType);
			}

			gotApplied = true;
		}

		return gotApplied;
	}
}
